<template>
  <div class="klaviyo-sign-up-form">
    <form
      :id="`klaviyo-sign-up-form__form--${id}`"
      ref="form"
      action="//manage.kmail-lists.com/subscriptions/subscribe"
      data-ajax-submit="//manage.kmail-lists.com/ajax/subscriptions/subscribe"
      method="GET"
      :name="`klaviyo-sign-up-form__form--${id}`"
      target="_blank"
      novalidate="novalidate"
      @submit="handleSubmit"
    >
      <input
        type="hidden"
        name="g"
        :value="listId"
      >
      <input
        type="hidden"
        name="$fields"
        value="$consent"
      >
      <input
        type="hidden"
        name="$list_fields"
        value="$consent"
      >
      <input
        type="hidden"
        name="$source"
        :value="source"
      >
      <input
        type="hidden"
        name="consent"
        value="SUBSCRIBED"
      >
      <input
        type="hidden"
        name="$consent"
        value="SUBSCRIBED"
      >
      <input
        :value="email"
        type="hidden"
        name="$email"
      >
      <div
        v-if="!isSubmitted"
        class="klaviyo_field_group klaviyo-sign-up-form__field"
        :class="{
          'has-error': hasError,
        }"
      >
        <label
          :for="`klaviyo-sign-up-form__email--${id}`"
          class="visually-hidden"
          v-text="emailPlaceholderText"
        />
        <div class="klaviyo-sign-up-form__email-wrapper">
          <input
            :id="`klaviyo-sign-up-form__email--${id}`"
            ref="email"

            :placeholder="emailPlaceholderText"
            type="email"
            name="email"
            class="klaviyo-sign-up-form__email text-body-2xs-regular"
            :value="email"
            @keyup="handleEmailKeyup"
            @change="handleEmailChange"
          >
          <button v-if="submitText.trim() !== ''"
            type="submit"
            class="klaviyo-sign-up-form__submit button klaviyo_submit_button"
            :disabled="isDisabled"
            v-text="submitText"
          />
          <button v-else
            type="submit"
            class="klaviyo-sign-up-form__submit button klaviyo_submit_button"
            :disabled="isDisabled"
          >
            <icon-arrow-right />
          </button>
        </div>
        <div
          class="error-message text-body-2xs-regular"
          v-text="errorMessage"
        />
      </div>
      <div class="klaviyo_messages text-body-2xs-regular">
        <div
          class="success_message"
          v-text="successMessage"
        />
        <div
          class="error_message"
          v-text="errorMessage"
        />
      </div>
    </form>
  </div>
</template>

<script>
import {
  newsletterSubscription as DYNewsletterSubscriptionEvent,
} from '~/helpers/dynamic-yield'
import IconArrowRight from '~icons/directional-navigation/chevron/right.svg'

/**
 * Vue: Klaviyo sign up form (klaviyo-sign-up-form)
 * -----------------------------------------------------------------------------
 * Klaviyo email sign up form.
 *
 * @param {String} id - A unique ID for the form.
 * @param {String} listId - The Klaviyo list ID to register the email to,
 * @param {String|null} hightouchSource - The source to send to Hightouch,
 * @param {String} source - The source to send to Klaviyo,
 * @param {String} customEmail - A custom email to register,
 * @param {String} emailPlaceholderText - The email field's placeholder text,
 * @param {String} submitText - The submit btn text,
 * @param {String} errorMessage - The email format / generic error message,
 * @param {String} successMessage - The success message,
 *
 */
export default {
  name: 'KlaviyoSignUpForm',
  components: {
    IconArrowRight,
  },
  props: {
    hightouchSource: {
      type: String,
      default: null,
    },
    id: {
      type: String,
      required: true,
    },
    listId: {
      type: String,
      required: true,
    },
    source: {
      type: String,
      default: 'Email sign up form',
    },
    customEmail: {
      type: String,
      default: '',
    },
    emailPlaceholderText: {
      type: String,
      default: 'Email Address',
    },
    submitText: {
      type: String,
      default: 'OK',
    },
    errorMessage: {
      type: String,
      default: 'Invalid email address. Please try again.',
    },
    successMessage: {
      type: String,
      default: 'You were successfully registered!',
    },
  },

  data() {
    return {
      email: '',
      emailRegex: /^([A-Z0-9_+-]+\.?)*[A-Z0-9_+-]@([A-Z0-9][A-Z0-9-]*\.)+[A-Z]{2,}$/i,
      hasError: false,
      isDisabled: true,
      isSubmitted: false,
    }
  },

  watch: {

    /**
     * Updates internal email when custom email passed in
     */
    customEmail(newCustomEmail) {
      const isEmail = this.emailRegex.test(newCustomEmail)
      this.email = newCustomEmail
      this.isDisabled = !isEmail
      this.hasError = !isEmail
    },
  },

  mounted() {
    // Attach Klaviyo SDK to form markup within section
    window.KlaviyoSubscribe?.attachToForms(`#klaviyo-sign-up-form__form--${this.id}`, {
      hide_form_on_success: true,
      custom_success_message: true,
      extra_properties: {
        $consent: 'SUBSCRIBED',
        $source: this.source,
        $method_type: 'Klaviyo Form',
        $method_id: 'embed',
        $consent_version: 'Embed default text',
      },
      success: () => {
        this.isDisabled = true
        this.isSubmitted = true

        if (window.htevents) {
          window.htevents.track(
            'Email Subscribed',
            {
              position: this.hightouchSource || this.source,
              email: this.email,
            },
            { sent_from: 'Hightouch JS' },
          )

          window.htevents.identify({
            email: this.email,
          }, { sent_from: 'Hightouch JS' })
        }

        DYNewsletterSubscriptionEvent(this.$refs.email?.value)
      },
    })
  },

  methods: {

    /**
     *
     * Submits the component's form
     */
    async forceEmailRegistration() {
      await this.$nextTick()

      if (this.isDisabled || this.hasError) {
        return
      }

      this.$refs.form?.dispatchEvent(new Event('submit'))
    },

    /**
     * Updates email and error state on change
     * @param event - Change event
     */
    handleEmailChange(event) {
      const isEmail = this.emailRegex.test(event.target.value)
      this.email = event.target.value
      this.isDisabled = !isEmail
      this.hasError = !isEmail
    },

    /**
     * Updates email and error state on keyup
     * @param event - Change event
     */
    handleEmailKeyup(event) {
      const isEmail = this.emailRegex.test(event.target.value)
      this.email = event.target.value
      this.isDisabled = !isEmail

      if (isEmail) {
        this.hasError = false
      }
    },

    /**
     * Handles a form submission
     * @param event - Change event
     */
    handleSubmit() {
      this.isDisabled = true
    },
  },
}
</script>

<style lang="scss">
@import './klaviyo-sign-up-form';
</style>
